export interface SquarifyInput {
  label: string;
  value: number;
}

export interface SquarifyRect {
  label: string;
  value: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

function worst(row: number[], w: number, _totalArea: number): number {
  const s = row.reduce((a, b) => a + b, 0);
  const maxR = Math.max(...row);
  const minR = Math.min(...row);
  const s2 = s * s;
  const w2 = w * w;
  return Math.max((w2 * maxR) / s2, s2 / (w2 * minR));
}

export function squarify(
  items: SquarifyInput[],
  bounds: { x: number; y: number; w: number; h: number }
): SquarifyRect[] {
  if (items.length === 0) return [];

  const totalValue = items.reduce((s, i) => s + i.value, 0);
  if (totalValue === 0) return [];

  const totalArea = bounds.w * bounds.h;
  const sorted = [...items].sort((a, b) => b.value - a.value);
  const areas = sorted.map((i) => (i.value / totalValue) * totalArea);

  const rects: SquarifyRect[] = [];
  let { x, y, w, h } = bounds;

  function layoutRow(row: number[], labels: string[], values: number[]) {
    const rowArea = row.reduce((a, b) => a + b, 0);
    const isHorizontal = w >= h;
    const side = isHorizontal ? h : w;
    const rowLen = rowArea / side;

    for (let i = 0; i < row.length; i++) {
      const dim = row[i] / rowLen;
      if (isHorizontal) {
        rects.push({ label: labels[i], value: values[i], x, y, w: rowLen, h: dim });
        y += dim;
      } else {
        rects.push({ label: labels[i], value: values[i], x, y, w: dim, h: rowLen });
        x += dim;
      }
    }

    if (isHorizontal) {
      x += rowLen;
      w -= rowLen;
      y = bounds.y + (bounds.h - h);
    } else {
      y += rowLen;
      h -= rowLen;
      x = bounds.x + (bounds.w - w);
    }
    // Reset the stacking axis
    if (isHorizontal) {
      y = rects.length > 0 ? bounds.y + (bounds.h - h) : bounds.y;
    } else {
      x = rects.length > 0 ? bounds.x + (bounds.w - w) : bounds.x;
    }
  }

  let row: number[] = [];
  let rowLabels: string[] = [];
  let rowValues: number[] = [];
  let shortSide = Math.min(w, h);

  for (let i = 0; i < areas.length; i++) {
    const area = areas[i];
    const testRow = [...row, area];

    if (
      row.length === 0 ||
      worst(testRow, shortSide, totalArea) <= worst(row, shortSide, totalArea)
    ) {
      row.push(area);
      rowLabels.push(sorted[i].label);
      rowValues.push(sorted[i].value);
    } else {
      layoutRow(row, rowLabels, rowValues);
      shortSide = Math.min(w, h);
      row = [area];
      rowLabels = [sorted[i].label];
      rowValues = [sorted[i].value];
    }
  }

  if (row.length > 0) {
    layoutRow(row, rowLabels, rowValues);
  }

  return rects;
}
